import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ProductService } from '../../../../../../../../SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/product/services/product.service';
import { CurrentTenantService } from '../../../../../../../../SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/tenant/services/current-tenant.service';
import { ApiService } from '../../../shared/services/api.service';
import type { IDashboard } from '../interfaces/dashboard.interface';
import type { ILatestDraftsPaginationResponse } from '../interfaces/latest-drafts-pagination-response.interface';
import { Dashboard } from '../models/dashboard.model';
import { DashboardSettingsService } from './dashboards-settings.service';
import * as featureMapping from './feature-mapping.json';

@Injectable({ providedIn: 'root' })
export class DashboardService extends ApiService<IDashboard> {
  private allLatestDrafts: Dashboard[];

  private readonly cachedDashboards: Record<string, IDashboard> = {};

  private normalizeDashboardPayload(dashboard: IDashboard): IDashboard {
    if (dashboard.displayIcon?.trim() === '') {
      const { displayIcon, ...rest } = dashboard;
      return rest;
    }

    return dashboard;
  }

  constructor(
    httpClient: HttpClient,
    private readonly productService: ProductService,
    private readonly dashboardSettingsService: DashboardSettingsService,
    private readonly tenantService: CurrentTenantService,
  ) {
    super(httpClient, dashboardSettingsService.dataServiceApi, 'dashboards');
  }

  async getAllLatestDrafts(): Promise<IDashboard[]> {
    return (this.allLatestDrafts ??= await this.get(
      `/latestDrafts/${this.tenantService.currentTenantData._id}`,
    ));
  }

  async getLatestDashboardByBaseId(
    baseId: string,
    includeUnpublished = false,
  ): Promise<IDashboard> {
    try {
      const dashboard = (this.cachedDashboards[baseId] ??= (
        await this.get(`${includeUnpublished ? '/admin' : ''}/getHistory/latest/${baseId}`)
      )[0]);

      if (dashboard) {
        return JSON.parse(JSON.stringify(dashboard));
      }
    } catch {}
  }

  getPaginatedLatestDrafts(paginationOptions: {
    pageSize: number;
    pageIndex: number;
  }): Promise<ILatestDraftsPaginationResponse> {
    return this.get(
      `/latestDrafts/paginated/${this.tenantService.currentTenantData._id}?pagination=${JSON.stringify(
        paginationOptions,
      )}`,
    );
  }

  async processTilesAgainstSubs(dashboard: IDashboard): Promise<void> {
    // TODO: Put in some kind of utility service
    function toEntries<T>(a: T[]): (readonly [number, T])[] {
      return a.map((value, index) => [index, value] as const);
    }

    const theDashboard: any = dashboard;
    theDashboard.canView = true;
    let foundPaidTile = false;

    for (const [i, section] of toEntries(dashboard.sections)) {
      if (section?.rows) {
        for (const [x, row] of toEntries(section.rows)) {
          if (row?.columns) {
            for (const [y, column] of toEntries(row.columns)) {
              const theColumn = column as any;
              if (theColumn?.tileDefinition && theColumn?.tileDefinition?.recipe) {
                if (!foundPaidTile) {
                  theDashboard.canView = false;
                  foundPaidTile = true;
                }
                const tag =
                  'DATASOURCE:' + theColumn.tileDefinition.recipe.dataSource.toUpperCase();
                theColumn.tileDefinition.canView = false;

                const ownedProducts = this.productService.ownedProductsList.getValue();

                for (const ownedProduct of ownedProducts) {
                  if (
                    ownedProduct.tags.includes(tag) &&
                    featureMapping[theColumn.tileDefinition.tileType] ===
                      ownedProduct.featureShortcode
                  ) {
                    theColumn.tileDefinition.canView = true;
                    theDashboard.canView = true;
                  }
                }
              } else {
                if (theColumn.tileDefinition) theColumn.tileDefinition.canView = true;
              }
            }
          }
        }
      }
    }
  }

  saveDashboard(dashboard: IDashboard) {
    // Ensure not published until deliberately published
    dashboard.published = false;
    return this.post(this.normalizeDashboardPayload(dashboard), `/drafts`);
  }

  createDashboard(dashboard: IDashboard) {
    return this.post(this.normalizeDashboardPayload(dashboard));
  }

  publishDraft(dashboard: IDashboard) {
    return lastValueFrom(
      this.httpClient.patch(
        `${this.baseUrl}/published/${dashboard.baseId}?version=${++dashboard.version}`,
        {},
      ),
    );
  }

  deleteDraft(id: string): Promise<void> {
    return this.delete(null, `/${id}`);
  }

  deleteAllDrafts(baseId: string): Promise<void> {
    return this.delete(null, `/drafts/${baseId}`);
  }

  async getLatestPublicDashboardByBaseId(baseId: string): Promise<Dashboard> {
    try {
      const dashboard = (this.cachedDashboards[baseId] ??= (
        await lastValueFrom(
          this.httpClient.get<IDashboard>(
            `${this.dashboardSettingsService.dataServiceApi}public-dashboards/${this.tenantService.currentTenantData._id}/?dashboardBaseIds=${baseId}`,
          ),
        )
      )[0]);

      if (dashboard) {
        return JSON.parse(JSON.stringify(dashboard));
      }
    } catch {}
  }
}
